import { Granularity, WindowKey } from '../../services/executionStatsService';

/**
 * Time windows for run statistics.
 *
 * Kept apart from caseWorkflowWindows: badges need a 24h window it doesn't
 * have, and its "all time" can't be charted by day inside the API's caps.
 */

/** Rolling windows ending now, for badges. */
export const BADGE_WINDOWS: { key: WindowKey; label: string }[] = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' }
];

/** Each badge window's length, for turning one into a from/to range ending now. */
export const WINDOW_MS: Record<WindowKey, number> = { '24h': 864e5, '7d': 7 * 864e5, '30d': 30 * 864e5 };

export const windowLabel = (key: WindowKey): string =>
  ({ '24h': 'last 24 hours', '7d': 'last 7 days', '30d': 'last 30 days' }[key]);

export interface DashboardRange {
  key: string;
  label: string;
  days: number;
  /** Granularities that stay inside the API's period caps (day 92, week 104, month 36). */
  granularities: Granularity[];
  defaultGranularity: Granularity;
}

export const DASHBOARD_RANGES: DashboardRange[] = [
  { key: '7d', label: 'Last 7 days', days: 7, granularities: ['day'], defaultGranularity: 'day' },
  { key: '30d', label: 'Last 30 days', days: 30, granularities: ['day', 'week'], defaultGranularity: 'day' },
  { key: '90d', label: 'Last 90 days', days: 90, granularities: ['day', 'week', 'month'], defaultGranularity: 'week' },
  { key: '12m', label: 'Last 12 months', days: 365, granularities: ['week', 'month'], defaultGranularity: 'month' }
];

export const DEFAULT_RANGE = '30d';

export const GRANULARITY_LABELS: Record<Granularity, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month'
};

export const rangeFor = (key: string | null | undefined): DashboardRange =>
  DASHBOARD_RANGES.find((r) => r.key === key) ?? DASHBOARD_RANGES.find((r) => r.key === DEFAULT_RANGE)!;

/**
 * [from, to) for a range ending now.
 *
 * `to` is rounded up to the next minute so a dashboard re-rendered within the
 * same minute asks the same question and hits the same cache.
 */
export const rangeBounds = (range: Pick<DashboardRange, 'days'>, now = Date.now()): { from: string; to: string } => {
  const to = Math.ceil(now / 60000) * 60000;
  return {
    from: new Date(to - range.days * 864e5).toISOString(),
    to: new Date(to).toISOString()
  };
};

/** A period's label on the axis. The value is already a local date, so it is sliced, never parsed. */
export const periodLabel = (period: string, granularity: Granularity): string => {
  if (granularity === 'month') {
    const [y, m] = period.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }
  const [y, m, d] = period.split('-').map(Number);
  const label = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return granularity === 'week' ? `w/c ${label}` : label;
};
